import { Team } from '@/types';

interface TeamChipProps {
  team: Team;
  showFullName?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function TeamChip({ team, showFullName = false, size = 'md', className = '' }: TeamChipProps) {
  const sizeClasses = {
    sm: 'px-2 py-1.5 gap-1.5',
    md: 'px-3 py-2 gap-2',
    lg: 'px-4 py-2.5 gap-2.5',
  };

  const logoSize = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  const colorSize = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const textSize = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  // Get display name - prefer shortName, fallback to name, then to '???'
  const displayName = showFullName ? team.name : (team.shortName || team.name || '???');

  return (
    <div
      className={`flex items-center bg-surface rounded-lg border border-border transition-all ${sizeClasses[size]} ${className}`}
      title={showFullName ? undefined : team.name}
    >
      {team.logoUrl ? (
        <img
          src={team.logoUrl}
          alt={`${team.shortName || team.name} logo`}
          className={`${logoSize[size]} rounded object-contain`}
          onError={(e) => {
            // Fallback to color indicator if logo fails to load
            e.currentTarget.style.display = 'none';
            const parent = e.currentTarget.parentElement;
            if (parent) {
              const fallback = document.createElement('span');
              fallback.className = `${colorSize[size]} rounded-full flex-shrink-0`;
              fallback.style.backgroundColor = team.color;
              parent.insertBefore(fallback, e.currentTarget.nextSibling);
            }
          }}
        />
      ) : (
        <span
          className={`${colorSize[size]} rounded-full flex-shrink-0`}
          style={{ backgroundColor: team.color }}
        />
      )}
      <span className={`font-semibold ${textSize[size]}`} style={{ color: team.color }}>
        {displayName}
      </span>
    </div>
  );
}
